export const FileTypeOptions = ['pdf', 'image', 'others'] as const;
export type FileType = (typeof FileTypeOptions)[number];

export const mimeTypeMapping: Record<string, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'application/pdf': 'PDF',
  'text/plain': 'Text',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'Word (DOCX)',
  'text/csv': 'CSV',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    'Excel (XLSX)',
};

export const allowedTypes = Object.keys(mimeTypeMapping);

export function getReadableType(mimeType: string): string {
  return mimeTypeMapping[mimeType] || mimeType;
}

export function getFileSize(size: number): string {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }

  return `${(size / 1024 / 1024).toFixed(2)} MB`;
}

export const getFileType = (mimeType: string): FileType => {
  if (mimeType.startsWith('image/')) {
    return 'image';
  }

  if (mimeType === 'application/pdf') {
    return 'pdf';
  }

  return 'others';
};

import { useTranslation } from 'react-i18next';

import ImageIcon from '@/shared/assets/document_image_icon.svg?react';
import OtherIcon from '@/shared/assets/document_others_icon.svg?react';
import PdfIcon from '@/shared/assets/document_pdf_icon.svg?react';

export interface FileTypeDisplay {
  label: string;
  icon: React.ReactNode;
}

export const useFileTypeDisplay = () => {
  const { t } = useTranslation('document');
  const getFileTypeDisplay = (fileType: FileType): FileTypeDisplay => {
    switch (fileType) {
      case 'pdf':
        return {
          label: t('file_type_pdf'),
          icon: <PdfIcon className="h-6 w-6" />,
        };
      case 'image':
        return {
          label: t('file_type_image'),
          icon: <ImageIcon className="h-6 w-6" />,
        };
      default:
        return {
          label: t('file_type_others'),
          icon: <OtherIcon className="h-6 w-6" />,
        };
    }
  };
  return { getFileTypeDisplay };
};
